// How the CRM says that a revenue figure is net of refunds.
//
// Since 2026-10-13 the CRM backend returns revenue NET of refunds, with the
// refund counted against the month of the original sale (Casey, 2026-10-12).
// Every such payload carries `refunds`:
//
//   'ok'           the figures are net; `gross` / `refunded` ride beside them
//   'unavailable'  Order.Refunded_Amount__c could not be read, so the figures
//                  are GROSS and every `refunded` is null
//   (absent)       a backend from before the change — it made no claim, so
//                  this page makes none either
//
// The one rule: a gross figure is never shown under a "net" label, and an
// unknown refund is never shown as $0. Everything here is a pure function of
// the payload so the wording can be tested without rendering a page.

const UNAVAILABLE_TITLE =
  'Refunds could not be read from Salesforce, so these figures still include refunded sales.';
const SALE_MONTH_NOTE = 'A refund counts against the month of the original sale.';

// A number, or null. null / undefined / '' are NOT zero here: Number(null) is
// 0, and "we were not told" must never print as "$0.00".
function num(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function money(v) {
  const n = num(v);
  if (n == null) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Compact money for a KPI subline: no cents from $100 up, "k" from $10,000.
export function moneyCompact(v) {
  const n = num(v);
  if (n == null) return '—';
  if (Math.abs(n) >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  if (Math.abs(n) >= 100) return `$${Math.round(n).toLocaleString('en-US')}`;
  return money(n);
}

/** 'ok' | 'unavailable' | 'unknown' — from any payload that carries `refunds`. */
export function refundsState(payload) {
  const v = payload?.refunds;
  if (v === 'ok') return 'ok';
  if (v === 'unavailable') return 'unavailable';
  return 'unknown';
}

/**
 * The phrase that goes beside a heading: what kind of figure this is.
 * '' when the backend said nothing, so an older backend is never labelled net.
 */
export function netLabel(state) {
  if (state === 'ok') return 'net of refunds';
  if (state === 'unavailable') return 'gross — refunds not taken off';
  return '';
}

/** Hover text for that phrase. `reason` is the backend's own explanation. */
export function netLabelTitle(state, reason) {
  if (state === 'ok') return `Revenue is sales minus refunds. ${SALE_MONTH_NOTE}`;
  if (state === 'unavailable') return reason || UNAVAILABLE_TITLE;
  return undefined;
}

/**
 * What came off ONE figure, for a subline and its hover text.
 *
 *   net, something refunded   { text: '−$20.10 refunded', title: '$360.30 sold − $20.10 refunded = $340.20 …' }
 *   net, nothing refunded     null — there is nothing to say
 *   refunds unavailable       { text: 'gross', title: …, warn: true }
 *   backend said nothing      null
 *
 * `compact` drops cents from $100 up, for the KPI strip.
 */
export function refundedNote({ state, gross, refunded }, { compact = false } = {}) {
  if (state === 'unavailable') return { text: 'gross', title: UNAVAILABLE_TITLE, warn: true };
  if (state !== 'ok') return null;
  const back = num(refunded);
  if (back == null || back <= 0) return null;
  const fmt = compact ? moneyCompact : money;
  const g = num(gross);
  const title = g == null
    ? `${money(back)} refunded. ${SALE_MONTH_NOTE}`
    : `${money(g)} sold − ${money(back)} refunded = ${money((Math.round(g * 100) - Math.round(back * 100)) / 100)}. ${SALE_MONTH_NOTE}`;
  return { text: `−${fmt(back)} refunded`, title, warn: false };
}

/**
 * One order row: the amount to show, and what came off it.
 *
 * The backend sends TotalAmount (the Order's gross total), refundedAmount and
 * netAmount. With refunds unavailable — or a backend that sent neither — the
 * amount is the gross total and `refunded` is null: unknown, not zero.
 *
 * @returns {{amount:number|null, refunded:number|null, title:string|undefined}}
 */
export function orderAmount(order, state) {
  const gross = num(order?.TotalAmount);
  if (state !== 'ok') return { amount: gross, refunded: null, title: state === 'unavailable' ? UNAVAILABLE_TITLE : undefined };
  const net = num(order?.netAmount);
  const back = num(order?.refundedAmount);
  if (net == null) return { amount: gross, refunded: null, title: undefined };
  if (back == null || back <= 0) return { amount: net, refunded: 0, title: undefined };
  return { amount: net, refunded: back, title: `${money(gross)} charged − ${money(back)} refunded` };
}

export const _text = { UNAVAILABLE_TITLE, SALE_MONTH_NOTE };
